// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Divider, Grid, Group, Loader, Select, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { Coverage, Organization, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  isPolicyholder,
  POLICYHOLDER_LABELS,
  policyholderFromCoverage,
  subscriberRelationshipFor,
} from '@polarissleep/shared';
import { IconCheck } from '@tabler/icons-react';
import { type JSX, useEffect, useState } from 'react';
import { FormCard } from '../../../components/FormCard';
import { useRole } from '../../../hooks/useRole';
import { getGroupId, getPlanName, setGroupId, setPlanName } from '../../../utils/patient-coverage';
import { hasPermission } from '../../../utils/permissions';
import { withTenantScope } from '../../../utils/tenant';
import { InsuranceCardPhotos } from './InsuranceCardPhotos';

interface FormValues {
  insuranceType: string;
  memberId: string;
  groupNumber: string;
  policyholder: string;
  secondaryInsuranceType: string;
  secondaryMemberId: string;
}

const PLAN_OPTIONS = [
  'CareFirst BlueCross BlueShield District of Columbia',
  'CareFirst Blue Cross Blue Shield Maryland',
  'CareFirst BlueCross BlueShield Medicare Advantage',
  'Blue Cross Blue Shield (Other)',
  'United Healthcare',
  'Aetna',
  'Other',
];

function buildInitialValues(
  coverage: Coverage | undefined,
  secondaryCoverage: Coverage | undefined,
  payor: Organization | undefined
): FormValues {
  return {
    insuranceType: coverage ? payor?.name || coverage.payor?.[0]?.display || getPlanName(coverage) : '',
    memberId: coverage?.subscriberId ?? '',
    groupNumber: coverage ? getGroupId(coverage) : '',
    policyholder: policyholderFromCoverage(coverage) ?? 'self',
    secondaryInsuranceType: secondaryCoverage
      ? secondaryCoverage.payor?.[0]?.display || getPlanName(secondaryCoverage)
      : '',
    secondaryMemberId: secondaryCoverage?.subscriberId ?? '',
  };
}

async function setPayor(
  medplum: ReturnType<typeof useMedplum>,
  coverage: Coverage,
  insuranceType: string,
  currentPayor?: Organization
): Promise<Coverage> {
  const name = insuranceType.trim();
  if (!name) {
    return { ...coverage, payor: [] };
  }

  const existingPayorName = currentPayor?.name ?? coverage.payor?.[0]?.display;
  if (existingPayorName === name && coverage.payor?.length) {
    return coverage;
  }

  const matchedPayor =
    currentPayor?.name === name ? currentPayor : await medplum.searchOne('Organization', { 'name:exact': name });
  return {
    ...coverage,
    payor: matchedPayor?.id ? [createReference(matchedPayor)] : [{ display: name }],
  };
}

export interface InsuranceSectionProps {
  patient: Patient;
  coverage: Coverage | undefined;
  secondaryCoverage?: Coverage;
  payor: Organization | undefined;
  loading: boolean;
  onSaved: (coverage: Coverage, payor?: Organization, secondaryCoverage?: Coverage) => void;
}

export function InsuranceSection({
  patient,
  coverage,
  secondaryCoverage,
  payor,
  loading,
  onSaved,
}: InsuranceSectionProps): JSX.Element {
  const medplum = useMedplum();
  const { role, isAdmin } = useRole();
  const canEdit = hasPermission({ role, permission: 'edit-patient-profile', isAdmin });

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const initialValues = buildInitialValues(coverage, secondaryCoverage, payor);
  const form = useForm<FormValues>({
    initialValues,
    validate: {
      memberId: (v) => (v.trim() ? null : 'Member ID is required'),
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: resync when coverage/payor load; form and isEditing guard against overwriting in-flight edits
  useEffect(() => {
    if (!isEditing) {
      const next = buildInitialValues(coverage, secondaryCoverage, payor);
      form.setValues(next);
      form.resetDirty(next);
    }
  }, [coverage, secondaryCoverage, payor]);

  const handleCancel = (): void => {
    form.reset();
    setIsEditing(false);
  };

  const handleSave = form.onSubmit(async (values) => {
    setSaving(true);
    try {
      let next: Coverage = coverage
        ? { ...coverage }
        : {
            resourceType: 'Coverage',
            status: 'active',
            beneficiary: { reference: `Patient/${patient.id}` },
            subscriber: { reference: `Patient/${patient.id}` },
            payor: [],
          };

      next.subscriberId = values.memberId.trim() || undefined;
      next = await setPayor(medplum, next, values.insuranceType, payor);
      // Keep the carrier in the legacy Coverage.class plan field for compatibility
      // with downstream consumers; the displayed value is sourced from Coverage.payor.
      next = setPlanName(next, values.insuranceType.trim());
      next = setGroupId(next, values.groupNumber.trim());
      // Rewrite the relationship only when it changes, so codes intake stored are left alone.
      if (isPolicyholder(values.policyholder) && values.policyholder !== policyholderFromCoverage(coverage)) {
        next.relationship = { coding: [subscriberRelationshipFor(values.policyholder)] };
      }

      let nextSecondary: Coverage | undefined;
      if (values.secondaryInsuranceType.trim() || values.secondaryMemberId.trim()) {
        const secondaryToSave: Coverage = secondaryCoverage
          ? { ...secondaryCoverage }
          : {
              resourceType: 'Coverage',
              status: 'active',
              beneficiary: { reference: `Patient/${patient.id}` },
              subscriber: { reference: `Patient/${patient.id}` },
              payor: [],
            };
        secondaryToSave.subscriberId = values.secondaryMemberId.trim() || undefined;
        nextSecondary = await setPayor(medplum, secondaryToSave, values.secondaryInsuranceType);
        nextSecondary = setPlanName(nextSecondary, values.secondaryInsuranceType.trim());
        nextSecondary = secondaryCoverage
          ? await medplum.updateResource(nextSecondary)
          : await medplum.createResource<Coverage>(withTenantScope(nextSecondary));
      }

      const saved = coverage
        ? await medplum.updateResource(next)
        : await medplum.createResource<Coverage>(withTenantScope(next));

      onSaved(saved, payor, nextSecondary);
      setIsEditing(false);
      showNotification({ color: 'green', icon: <IconCheck size={16} />, message: 'Insurance updated' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(false);
    }
  });

  let action: JSX.Element | null = null;
  if (isEditing) {
    action = (
      <Group gap="xs">
        <Button size="xs" variant="default" onClick={handleCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="xs" onClick={() => handleSave()} loading={saving}>
          Save
        </Button>
      </Group>
    );
  } else if (canEdit) {
    action = (
      <Button size="xs" onClick={() => setIsEditing(true)}>
        Edit
      </Button>
    );
  }

  if (loading) {
    return (
      <FormCard title="Insurance">
        <Stack align="center" py="md">
          <Loader size="sm" />
        </Stack>
      </FormCard>
    );
  }

  return (
    <FormCard title="Insurance" isEditing={isEditing} action={action}>
      <Stack gap="md">
        <Text fw={600} size="sm">
          Primary Insurance
        </Text>
        <Divider />
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Select
              aria-label="Primary Insurance Type"
              label="Insurance Type"
              data={[...new Set([...PLAN_OPTIONS, form.values.insuranceType])]}
              readOnly={!isEditing}
              {...form.getInputProps('insuranceType')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput
              aria-label="Primary Member ID"
              label="Member ID"
              readOnly={!isEditing}
              {...form.getInputProps('memberId')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput label="Group Number" readOnly={!isEditing} {...form.getInputProps('groupNumber')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Select
              aria-label="Primary Policy holder"
              label="Policy holder"
              data={Object.entries(POLICYHOLDER_LABELS).map(([value, label]) => ({ value, label }))}
              readOnly={!isEditing}
              {...form.getInputProps('policyholder')}
            />
          </Grid.Col>
          {coverage?.id && (
            <Grid.Col span={12}>
              <Stack gap="xs">
                <Text fw={600} size="sm">
                  Insurance Card
                </Text>
                <InsuranceCardPhotos coverageId={coverage.id} />
              </Stack>
            </Grid.Col>
          )}
          <Grid.Col span={12}>
            <Text fw={600} size="sm">
              Secondary Coverage
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Select
              aria-label="Secondary Insurance Type"
              label="Insurance Type"
              data={[...new Set([...PLAN_OPTIONS, form.values.secondaryInsuranceType])]}
              readOnly={!isEditing}
              {...form.getInputProps('secondaryInsuranceType')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput
              aria-label="Secondary Member ID"
              label="Member ID"
              readOnly={!isEditing}
              {...form.getInputProps('secondaryMemberId')}
            />
          </Grid.Col>
        </Grid>
      </Stack>
    </FormCard>
  );
}
